import { CMSData, CtaComponentProps } from "@/cms/models";
import { TypographyComponentProps } from "@/components/ui/TypographyComponent";
import {
  cornerLengths,
  isCornerLength,
  ownShadowCss,
} from "@/utils/form-values";
import { isDerivedV2, isLookSystem } from "@/utils/look-gate";
import { ownColourCss, ownColourInDark } from "@/utils/colour-versions";

// The colour bridge re-declares these per section background, so a classic button and text follow the background they
// sit on instead of the theme's single colour.
const LOOK_BUTTON = {
  textColor: "var(--button-text-color)",
  bgColor: "var(--button-bg-color)",
  hoverBgColor: "var(--button-hover-bg-color)",
  hoverTextColor: "var(--button-hover-text-color)",
};
// Derived v2 guarantees a press colour that differs from hover; a pill (DesignAction) shows it only when given.
const LOOK_BUTTON_PRESS = {
  activeBgColor: "var(--button-active-bg-color)",
  activeTextColor: "var(--button-active-text-color)",
};
const LOOK_PAGE = { textColor: "var(--page-text-color)" };

// Outside Tailwind's content globs: these reach the stylesheet through the safelist in tailwind.config.ts.
const OWN_RADIUS = "rounded-[var(--re-own-radius)]";
const OWN_RADIUS_DESKTOP = "md:rounded-[var(--re-own-radius-desktop)]";
// Without the `shadow:` hint Tailwind reads a var() as a shadow colour, not a shadow.
const OWN_SHADOW = "shadow-[shadow:var(--re-own-shadow)]";
const OWN_SHADOW_HOVER = "hover:shadow-[shadow:var(--re-own-shadow-hover)]";

/** Each of a button's states: its fill and the colours drawn on it. */
const BUTTON_STATES = [
  { fill: "bgColor", on: ["textColor", "borderColor"] },
  { fill: "hoverBgColor", on: ["hoverTextColor", "hoverBorderColor"] },
  { fill: "activeBgColor", on: ["activeTextColor", "activeBorderColor"] },
] as const;

/**
 * A button's own fill as its dark version where `derived.darkOwn` names it, and the text and border on it where that
 * fill changes in dark: a palette or role colour, or an own colour that takes its version. A fill that keeps its stored
 * code, `transparent` among them, keeps them as stored.
 */
function withDarkVersions(
  theme: unknown,
  props: CtaComponentProps,
): CtaComponentProps {
  const colours = { ...props } as unknown as Record<string, unknown>;
  for (const { fill, on } of BUTTON_STATES) {
    const value = colours[fill];
    if (typeof value !== "string") continue;
    colours[fill] = ownColourCss(theme, value);
    if (!value.includes("var(--") && !ownColourInDark(theme, value)) continue;
    for (const key of on) {
      const colour = colours[key];
      if (typeof colour === "string")
        colours[key] = ownColourCss(theme, colour);
    }
  }
  return colours as unknown as CtaComponentProps;
}

export function mergeCtaSettings(
  config: any,
  cmsData: CMSData,
  defaultValues?: Partial<CtaComponentProps>,
): CtaComponentProps {
  const lookSystem = isLookSystem(cmsData.theme);
  const button = lookSystem ? LOOK_BUTTON : cmsData.theme.button;
  const merged: CtaComponentProps = {
    ...{
      textColor: defaultValues?.textColor ?? button.textColor,
      bgColor: defaultValues?.bgColor ?? button.bgColor,
      borderColor: defaultValues?.borderColor ?? "inherit",
      hoverBgColor: defaultValues?.hoverBgColor ?? button.hoverBgColor,
      hoverTextColor: config.hoverTextColor ?? button.hoverTextColor,
      size: defaultValues?.size ?? "sm",
    },
    ...config,
    ...(lookSystem ? { lookSystem } : {}),
  };
  if (!lookSystem) return merged;
  const pressed =
    isDerivedV2(cmsData.theme) &&
    merged.bgColor === LOOK_BUTTON.bgColor &&
    merged.hoverBgColor === LOOK_BUTTON.hoverBgColor &&
    merged.activeBgColor === undefined
      ? { ...merged, ...LOOK_BUTTON_PRESS }
      : merged;
  return withDarkVersions(cmsData.theme, pressed);
}

export function mergeTypographySettings(
  config: any,
  cmsData: CMSData,
  defaultValues?: Partial<TypographyComponentProps>,
): TypographyComponentProps {
  const lookSystem = isLookSystem(cmsData.theme);
  const page = lookSystem ? LOOK_PAGE : cmsData.theme.page;
  return {
    ...{
      textColor: page.textColor,
      size: defaultValues?.size ?? "sm",
      padding: defaultValues?.padding ?? "none",
    },
    ...config,
    ...(lookSystem ? { lookSystem } : {}),
  };
}

function buildPadding(padding: string, onlyDesktop: boolean) {
  const paddings = padding.split(" ");
  const desktopPrefix = onlyDesktop ? `md:` : "";
  switch (paddings.length) {
    case 0:
      return "";
    case 1:
      return `${desktopPrefix}homepage-p-${paddings[0]}`;
    case 2:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pb-${paddings[0]} ${desktopPrefix}homepage-pl-${paddings[1]} ${desktopPrefix}homepage-pr-${paddings[1]}`;
    case 3:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pb-${paddings[0]} ${desktopPrefix}homepage-pl-${paddings[1]} ${desktopPrefix}homepage-pr-${paddings[1]}`;
    case 4:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pr-${paddings[1]} ${desktopPrefix}homepage-pb-${paddings[2]} ${desktopPrefix}homepage-pl-${paddings[3]}`;
  }
}

function buildMargin(margin: string, onlyDesktop: boolean) {
  const margins = margin.split(" ");
  const desktopPrefix = onlyDesktop ? `md:` : "";
  switch (margins.length) {
    case 0:
      return "";
    case 1:
      return `${desktopPrefix}homepage-m-${margins[0]}`;
    case 2:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mb-${margins[0]} ${desktopPrefix}homepage-ml-${margins[1]} ${desktopPrefix}homepage-mr-${margins[1]}`;
    case 3:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mb-${margins[0]} ${desktopPrefix}homepage-ml-${margins[1]} ${desktopPrefix}homepage-mr-${margins[1]}`;
    case 4:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mr-${margins[1]} ${desktopPrefix}homepage-mb-${margins[2]} ${desktopPrefix}homepage-ml-${margins[3]}`;
  }
}

export function getMargins(margin?: string) {
  if (!margin) {
    return "";
  }
  const responsiveMargins = margin.split("|");
  if (responsiveMargins.length === 1) {
    return buildMargin(responsiveMargins[0].trim(), false);
  }
  return (
    buildMargin(responsiveMargins[0].trim(), false) +
    " " +
    buildMargin(responsiveMargins[1].trim(), true)
  );
}

export function getTextSize(size?: string, defaultValue: string = "sm") {
  return `homepage-text-${getMobileSize(size, defaultValue)} md:homepage-text-${getDesktopSize(size, defaultValue)}`;
}

export function getMobileFlowDirection(direction?: string) {
  if (!direction) {
    return "vertical";
  }
  const responsiveDirection = direction.split("|");
  return responsiveDirection[0].trim();
}

export function getDesktopFlowDirection(direction?: string) {
  if (!direction) {
    return "vertical";
  }
  const responsiveDirection = direction.split("|");
  return responsiveDirection[1]?.trim() ?? responsiveDirection[0].trim();
}

export function getMobileSize(size?: string, defaultValue: string = "sm") {
  if (!size) {
    return defaultValue;
  }
  const responsiveSize = size.split("|");
  if (responsiveSize.length === 0) {
    return defaultValue;
  }
  return responsiveSize[0].trim();
}

export function getDesktopSize(size?: string, defaultValue: string = "sm") {
  if (!size) {
    return defaultValue;
  }
  const responsiveSize = size.split("|");
  if (responsiveSize.length === 1) {
    return responsiveSize[0].trim();
  }
  return responsiveSize[1].trim();
}

export function getMobileHeight(height?: string) {
  if (!height) {
    return "auto";
  }

  const responsiveHeight = height.split("|");
  if (responsiveHeight.length === 0) {
    return "auto";
  }

  return responsiveHeight[0].trim();
}

export function getDesktopHeight(height?: string) {
  if (!height) {
    return "auto";
  }
  const responsiveHeight = height.split("|");
  if (responsiveHeight.length === 1) {
    return responsiveHeight[0].trim();
  }
  return responsiveHeight[1].trim();
}

export type OwnSectionHeights = "none" | "mobile" | "desktop" | "both";

/** The devices on which a section has a height of its own for its content to fill: Fyld skærmhøjden (`ui.viewport`) on
 *  both, else Højde (`ui.height`, `mobile | desktop`). `auto`, nothing or a percentage, which a section in the page's
 *  flow cannot resolve, leaves a device to its content. */
export function ownSectionHeights(ui?: {
  height?: string;
  viewport?: boolean;
}): OwnSectionHeights {
  if (ui?.viewport) return "both";
  const own = (value: string) =>
    value !== "" && value !== "auto" && !value.endsWith("%");
  const mobile = own(getMobileHeight(ui?.height));
  const desktop = own(getDesktopHeight(ui?.height));
  return mobile && desktop
    ? "both"
    : mobile
      ? "mobile"
      : desktop
        ? "desktop"
        : "none";
}

type StoredColours = {
  ui?: { textColor?: string; bgColor?: string };
  specifics?: unknown;
};

/** The text colour a section stores for itself, read in SectionLayout's order (specifics.textColor and farvetekst are
 *  older places for it). */
export function ownSectionTextColor(section: StoredColours) {
  const specifics = (section.specifics ?? {}) as Record<string, unknown>;
  return (specifics.textColor ??
    section.ui?.textColor ??
    specifics.farvetekst) as string | undefined;
}

/** The background colour a section stores for itself, read in SectionLayout's order (specifics.bgColor and
 *  farvebaggrund are older places for it). */
export function ownSectionBgColor(section: StoredColours) {
  const specifics = (section.specifics ?? {}) as Record<string, unknown>;
  return (specifics.bgColor ??
    section.ui?.bgColor ??
    specifics.farvebaggrund) as string | undefined;
}

export function getPaddings(padding?: string) {
  if (!padding) {
    return "";
  }

  const responsivePaddings = padding.split("|");
  if (responsivePaddings.length === 1) {
    return buildPadding(responsivePaddings[0].trim(), false);
  }
  return (
    buildPadding(responsivePaddings[0].trim(), false) +
    " " +
    buildPadding(responsivePaddings[1].trim(), true)
  );
}

/** With `exact`, an exact length reads `--re-own-radius[-desktop]`, which the caller sets from `ownRadiusStyle`. */
export function getRounded(rounded?: string, exact = false) {
  if (!rounded) {
    return "rounded-none";
  }

  const mobile = getMobile("none", rounded);
  const desktop = getDesktop("none", rounded);
  const mobileClass =
    exact && cornerLengths(mobile)
      ? OWN_RADIUS
      : buildCorners(mobile, "", "rounded");
  const desktopClass =
    exact && cornerLengths(desktop)
      ? OWN_RADIUS_DESKTOP
      : buildCorners(desktop, "md:", "rounded");

  return `${mobileClass} ${desktopClass}`;
}

export function ownRadiusStyle(
  rounded?: string,
  exact = false,
): Record<string, string> {
  const style: Record<string, string> = {};
  if (!exact || !rounded) return style;
  const mobile = cornerLengths(getMobile("none", rounded));
  const desktop = cornerLengths(getDesktop("none", rounded));
  if (mobile) style["--re-own-radius"] = mobile;
  if (desktop) style["--re-own-radius-desktop"] = desktop;
  return style;
}

/** `<rest> | <hover>`. With `exact`, own CSS reads `--re-own-shadow[-hover]`, which the caller sets from
 *  `ownShadowStyle`. */
export function getShadow(shadow?: string, exact = false) {
  const rest = getMobile("none", shadow);
  const hover = getDesktop("none", shadow);
  const hoverClass =
    exact && ownShadowCss(hover)
      ? OWN_SHADOW_HOVER
      : `hover:${getShadowSingle(hover)}`;

  return `${getShadowSingle(rest, exact)} ${hoverClass} ease-in-out duration-300`;
}

export function ownShadowStyle(
  shadow?: string,
  exact = false,
): Record<string, string> {
  const style: Record<string, string> = {};
  if (!exact) return style;
  const rest = ownShadowCss(getMobile("none", shadow));
  const hover = ownShadowCss(getDesktop("none", shadow));
  if (rest) style["--re-own-shadow"] = rest;
  if (hover) style["--re-own-shadow-hover"] = hover;
  return style;
}

export function getShadowSingle(shadow?: string, exact = false) {
  switch (shadow) {
    case "none":
      return "";

    case "sm":
      return "shadow-sm";
    case "md":
      return "shadow-md";
    case "lg":
      return "shadow-lg";
    case "xl":
      return "shadow-xl";
    case "2xl":
      return "shadow-2xl";

    default:
      return exact && shadow && ownShadowCss(shadow) ? OWN_SHADOW : "";
  }
}

export function buildCorners(
  values: string,
  prefix: string,
  valueName: string,
) {
  const margins = values.split(" ");

  switch (margins.length) {
    case 0:
      return "";
    case 1:
      return `${prefix}${valueName}-${margins[0]}`;
    case 2:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[0]} ${prefix}${valueName}-bl-${margins[1]}`;
    case 3:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[2]} ${prefix}${valueName}-bl-${margins[1]}`;
    case 4:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[2]} ${prefix}${valueName}-bl-${margins[3]}`;
  }
}

export function getVisibleValue(boolValue?: string) {
  if (!boolValue) {
    return "";
  }

  const responsiveBooleanValues = boolValue.split("|");

  if (responsiveBooleanValues.length === 1) {
    return responsiveBooleanValues[0].trim() === "true"
      ? ""
      : "!hidden !md:hidden";
  }

  let visibilityClass =
    responsiveBooleanValues[0].trim() === "false" ? "hidden" : "flex";
  visibilityClass +=
    responsiveBooleanValues[1].trim() === "false" ? " md:hidden" : " md:flex";

  return visibilityClass;
}

export function getGap(gap?: string, defaultValue: string = "") {
  if (!gap) {
    if (!defaultValue) {
      return "";
    }

    return `homepage-gap-${defaultValue}`;
  }

  const responsiveGaps = gap.split("|");
  if (responsiveGaps.length === 1) {
    return `homepage-gap-${responsiveGaps[0].trim()}`;
  }
  return `homepage-gap-${responsiveGaps[0].trim()} md:homepage-gap-${responsiveGaps[1].trim()}`;
}

export function getTestimonialImageSize(size?: string) {
  switch (size) {
    case "sm":
      return "120px";
    case "md":
      return "160px";
    case "lg":
      return "200px";
    case "xl":
      return "240px";
    default:
      return "120px";
  }
}

export function getMobile(defaultValue: string, input?: string) {
  if (!input) {
    return defaultValue;
  }
  const responsiveHeight = input.split("|");

  return responsiveHeight[0].trim();
}

export function getDesktop(defaultValue: string, input?: string) {
  if (!input) {
    return defaultValue;
  }
  const responsiveHeight = input.split("|");
  if (responsiveHeight.length === 1) {
    return responsiveHeight[0].trim();
  }
  return responsiveHeight[1].trim();
}

export function getBorderRadius(
  rounded?: string,
  type?: string,
  exact = false,
) {
  if (!rounded) {
    return "inherit";
  }

  if (
    type === "image" ||
    type === "card" ||
    type === "video" ||
    type === "section"
  ) {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "2xl";
          default:
            return x;
        }
      })
      .join(" ");
  } else if (type === "button") {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "full";
          default:
            return x;
        }
      })
      .join(" ");
  } else {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "full";
          default:
            return x;
        }
      })
      .join(" ");
  }

  return rounded
    .split(" ")
    .map((x) => getBorderRadiusSingle(x, exact))
    .join(" ");
}

/** With `exact` (the look system), an exact length passes as stored instead of falling back to `inherit`. */
export function getBorderRadiusSingle(rounded?: string, exact = false) {
  switch (rounded) {
    case "none":
      return "0px";
    case "theme":
      return "inherit";
    case "sm":
      return "0.125rem";
    case "md":
      return "0.375rem";
    case "lg":
      return "0.5rem";
    case "xl":
      return "0.75rem";
    case "2xl":
      return "1rem";
    case "3xl":
      return "1.5rem";
    case "full":
      return "9999px";
    default:
      return exact && rounded && isCornerLength(rounded) ? rounded : "inherit";
  }
}

// A section's id doubles as its CSS class and as the selector for the rule that
// carries its colours and heights. An id the CSS parser rejects — one starting
// with a digit, such as the "404" the not-found fallback used — makes the whole
// rule invalid, so the section silently loses every variable it declares. Ids
// that are already valid identifiers are returned untouched, so no existing
// section changes class.
const CSS_IDENTIFIER = /^-?[_a-zA-Z][_a-zA-Z0-9-]*$/;

export function getSectionClassName(id?: string) {
  const value = id ?? "";
  if (CSS_IDENTIFIER.test(value)) {
    return value;
  }
  return `s-${value.replace(/[^_a-zA-Z0-9-]/g, "-")}`;
}
